import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { readImageEditStatus, waitForImageEditCheck } from "@/lib/image-edit-observation";
import { useQuery, useMutation } from "@tanstack/react-query";
import { VariantPicker } from "@/components/variant-picker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { AdImage } from "@/components/ad-image";
import { UndoAdEditButton, RedoAdEditButton, useUndoableAdIds, useRedoableAdIds, invalidateUndoableAds } from "@/components/undo-ad-edit";
import { InlineRenameTitle } from "@/components/inline-rename-title";
import { sanitizeFilename } from "@/lib/filename";
import { AdsExpressPreviousSessions } from "@/components/ads-express-previous-sessions";
import {
  AdsExpressRunQueuePanel,
  useAdsExpressRunQueue,
  invalidateAdsExpressRunQueue,
} from "@/components/ads-express-run-queue";
import {
  Zap, Loader2, XCircle, Download, RotateCcw, Sparkles,
  Package, Bookmark, BookmarkCheck, Shirt, Image as ImageIcon, Wand2,
  Plus, Pencil, Trophy, Tag as TagIcon, Archive,
  Search, Check, Megaphone, Users, Film, ChevronDown, CopyPlus,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { duplicateAdAsIs, copyAndReframeAd } from "@/lib/ad-copy";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Brand, Product, AdUnitType } from "@shared/schema";
import { type AdImageRecord, displayUrl, normalizeExampleImage } from "@shared/adImage";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS, normalizeAspectRatio } from "@shared/aspectRatios";
import { loadLastAspectRatio, saveLastAspectRatio } from "@/lib/aspectRatio";
import {
  rememberPendingImageEdit,
  findPendingImageEdit,
  clearPendingImageEdit,
  imageEditPhaseLabel,
  updatePendingImageEdit, persistPendingImageEdit,
} from "@/lib/pending-image-edits";
import { usePendingImageEditsRevision } from "@/hooks/use-pending-image-edits";
import { SearchableSelect } from "@/lib/picker-search";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import { apiRequest, getQueryFn, invalidateGeneratedAdListings, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
import {
  BUCKETS, PRESETS, bucketOf, shortNameOf,
  type BucketId, type PresetId,
} from "@/lib/static-ad-buckets";
import { BUCKET_VISUALS as BASE_BUCKET_VISUALS } from "@/lib/bucket-visuals";
import { useParams, useLocation, useSearch } from "wouter";

const KC_BUCKET_ID = "kill-crew" as const;
type KcBucketId = BucketId | typeof KC_BUCKET_ID;
const KC_SLUGS: ReadonlySet<string> = new Set([
  "centered-product--handwritten-headline",
  "feature-breakdown--product-hybrid-overlay",
  "dual-product-contrast--statement-typography",
  "centered-product-hero--color-variant-strip",
  "product-hero--streetwear-drop-hype",
  "dark-product-hero--handwritten-headline",
  "lifestyle-flat-lay--themed-color-story",
  "lifestyle-flat-lay--contextual-props",
  "product-hero--social-proof-comment",
  "centered-product--dark-void--accent-branding",
  "floating-product--environmental-depth--handwritten-overlay",
  "centered-product--soft-color-field--handwritten-overlay",
  "layered-variants--themed-scatter-props",
  "stacked-variants-close-up",
]);
const KC_BUCKET = {
  id: KC_BUCKET_ID,
  label: "Kill Crew Templates",
  description: "Curated template set for Kill Crew ads",
} as const;

function toImageRecordsAE(raw: unknown): AdImageRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => normalizeExampleImage(entry)).filter((r): r is AdImageRecord => r != null);
}

const BUCKET_VISUALS: Record<KcBucketId, { icon: React.ReactNode; accent: string; bar: string }> = {
  ...BASE_BUCKET_VISUALS,
  "kill-crew": { icon: <Zap className="h-4 w-4" />, accent: "bg-primary/15 text-primary", bar: "bg-primary" },
};

type ServerGenerationStatus = "queued" | "running" | "completed" | "partial" | "failed" | "cancelled";
type AdServerStatus = "pending" | "rendering" | "queued" | "ready" | "failed" | "cancelled";
interface ServerGeneration {
  id: string;
  status: ServerGenerationStatus;
  productId: string | null;
  selectedAdUnitIds: string[] | null;
  aspectRatio: string | null;
  creativeDirection: string | null;
}
interface ServerGeneratedAd {
  id: string;
  adUnitTypeId: string | null;
  // Template name captured at creation (survives template renames/deletes).
  adUnitNameSnapshot?: string | null;
  customName: string | null;
  status: AdServerStatus;
  imageUrlOriginal: string | null;
  // Durable R2 URL, present once the ad is saved. Preferred over the
  // expiring provider URL when editing so older ads stay editable.
  imageUrlSaved: string | null;
  errorMessage: string | null;
  isSaved: boolean;
  isWinner: boolean;
  archivedAt: string | null;
  tags: string[] | null;
}

const TERMINAL: ServerGenerationStatus[] = ["completed", "partial", "failed", "cancelled"];

const isKillCrewBrand = (b: Pick<Brand, "name" | "slug"> | undefined | null) => {
  if (!b) return false;
  const re = /kill\s*crew/i;
  return re.test(b.name ?? "") || re.test(b.slug ?? "");
};

interface KcSimConfigStatus {
  productTruthUrl: boolean;
  creativeAngleUrl: boolean;
  adPromptUrl: boolean;
  simApiKey: boolean;
}

export default function AdsExpressKcPage() {
  const imageEditsRevision = usePendingImageEditsRevision();
  const { brandId } = useParams<{ brandId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // ─── Form state — single step now ─────────────────────────────────────
  // Truths/Angles state intentionally removed: those tables/routes/worker
  // code remain dormant for legacy rows but the UI no longer surfaces
  // them. Cardinality of a run is templates × 1.
  const [productId, setProductId] = useState<string>("");
  // Task #744 — optional product variant selection. null = "no specific variant".
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [productSectionOpen, setProductSectionOpen] = useState<boolean>(true);
  const [collapsedBuckets, setCollapsedBuckets] = useState<Set<string>>(new Set());
  const [bucketsInitialized, setBucketsInitialized] = useState<boolean>(false);
  const [selectedAdUnitIds, setSelectedAdUnitIds] = useState<Set<string>>(new Set());
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(() => loadLastAspectRatio());
  const [searchTerm, setSearchTerm] = useState("");
  const [addMode, setAddMode] = useState(false);
  const [activeGenerationId, setActiveGenerationId] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, { imageUrl?: string | null; isSaved?: boolean; saving?: boolean; customName?: string | null }>>({});
  const [previewTile, setPreviewTile] = useState<{ id: string; imageUrl: string; name: string; templateName?: string | null } | null>(null);
  // Edit-in-preview state — the user types a steer in the expanded
  // preview dialog and it submits directly to the /api/edit-image
  // endpoint (the same one the pen icon uses on the thumbnail).
  const [editPrompt, setEditPrompt] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [editResultUrl, setEditResultUrl] = useState<string | null>(null);
  // Tiles with an inline image edit currently in flight. This drives the
  // loading overlay on BOTH the grid tile and the preview dialog, and — unlike
  // the old per-modal flag — it survives closing the modal so a background edit
  // keeps showing progress on the tile at the bottom and updates it when done.
  const [editingTileIds, setEditingTileIds] = useState<Set<string>>(() => new Set());
  // Coarse server-reported phase per in-flight tile edit (rendering →
  // saving); drives the overlay label alongside editingTileIds.
  const [tileEditPhases, setTileEditPhases] = useState<Record<string, string | null>>({});
  const [tileEditErrors, setTileEditErrors] = useState<Record<string, string>>({});
  // Ordered list of selected reference images. The first item is the
  // "primary" — sent first to the endpoint. Multi-select is allowed.
  const [selectedProductImages, setSelectedProductImages] = useState<string[]>([]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const scrollToResultsRequested = useRef(false);
  const [generating, setGenerating] = useState(false);

  // ─── Brand gate ──────────────────────────────────────────────────────
  const { data: brand, isLoading: loadingBrand, isError: brandError } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });
  const isKc = isKillCrewBrand(brand ?? null);

  // Non-KC brands cannot load this page — redirect to the brand home.
  useEffect(() => {
    if (!loadingBrand && brand && !isKc && brandId) {
      setLocation(`/brands/${brandId}`);
    }
  }, [loadingBrand, brand, isKc, brandId, setLocation]);

  // ─── KC Sim config status (brand-scoped — admin endpoint is master-only) ─
  // The single-step path only really needs SIM_AD_PROMPT_URL + the API
  // key, but we keep showing the full set so operators can still see
  // truth/angle URL status (legacy paths / future use).
  const { data: simStatus } = useQuery<KcSimConfigStatus>({
    queryKey: [`/api/brands/${brandId}/kc-sim-config-status`],
    enabled: !!brandId && isKc,
  });
  const simReady = !!simStatus && simStatus.adPromptUrl && simStatus.simApiKey;

  // ─── Admin: KC design prompt editor ──────────────────────────────────
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  const { data: kcPromptsData } = useQuery<{ prompts: Array<{ adUnitTypeId: string; designPrompt: string; useTemplateImage?: boolean }> }>({
    queryKey: ["/api/admin/kc-ad-unit-prompts"],
    enabled: isAdmin && isKc,
  });
  const promptByUnitId = useMemo(() => {
    const m = new Map<string, { designPrompt: string; useTemplateImage: boolean }>();
    for (const p of kcPromptsData?.prompts ?? []) {
      m.set(p.adUnitTypeId, {
        designPrompt: p.designPrompt ?? "",
        useTemplateImage: p.useTemplateImage ?? true,
      });
    }
    return m;
  }, [kcPromptsData]);
  const [editingUnit, setEditingUnit] = useState<AdUnitType | null>(null);
  const [draftPrompt, setDraftPrompt] = useState("");
  const [draftUseTemplateImage, setDraftUseTemplateImage] = useState(true);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const openPromptEditor = (unit: AdUnitType) => {
    const existing = promptByUnitId.get(unit.id);
    setEditingUnit(unit);
    setDraftPrompt(existing?.designPrompt ?? "");
    setDraftUseTemplateImage(existing?.useTemplateImage ?? true);
  };
  const savePromptEdit = async () => {
    if (!editingUnit) return;
    setSavingPrompt(true);
    try {
      await apiRequest("PUT", `/api/admin/kc-ad-unit-prompts/${editingUnit.id}`, {
        designPrompt: draftPrompt,
        useTemplateImage: draftUseTemplateImage,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/kc-ad-unit-prompts"] });
      toast({ title: "KC prompt saved" });
      setEditingUnit(null);
    } catch (e: unknown) {
      toast({
        title: "Save failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSavingPrompt(false);
    }
  };

  // ─── Lookups ─────────────────────────────────────────────────────────
  const { data: productsRaw = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId && isKc,
  });
  const products = useMemo(
    () => [...productsRaw].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })),
    [productsRaw],
  );

  const { data: adUnits = [] } = useQuery<AdUnitType[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId && isKc,
  });

  const staticAdUnits = useMemo(
    () => adUnits.filter((u) => u.status !== "archived" && u.formatType !== "ugc" && u.formatType !== "video" && u.formatType !== "pixar"),
    [adUnits],
  );

  const applyPreset = (presetId: PresetId) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const matched = staticAdUnits.filter((u) => u.slug && preset.slugs.includes(u.slug));
    const matchedIds = matched.map((u) => u.id);
    setSelectedAdUnitIds((prev) => {
      const next = addMode ? new Set(prev) : new Set<string>();
      matchedIds.forEach((id) => next.add(id));
      return next;
    });
    if (matched.length === 0) {
      toast({
        title: "No templates matched",
        description: "This brand's library doesn't include any templates from that preset.",
        variant: "destructive",
      });
    }
  };

  const clearAllAdUnits = () => setSelectedAdUnitIds(new Set());

  const selectedSlugs = useMemo(() => {
    const set = new Set<string>();
    staticAdUnits.forEach((u) => {
      if (u.slug && selectedAdUnitIds.has(u.id)) set.add(u.slug);
    });
    return set;
  }, [staticAdUnits, selectedAdUnitIds]);

  const activePreset: PresetId | null = useMemo(() => {
    for (const preset of PRESETS) {
      if (preset.slugs.length !== selectedSlugs.size) continue;
      if (preset.slugs.every((s) => selectedSlugs.has(s))) return preset.id;
    }
    return null;
  }, [selectedSlugs]);

  const groupedAdUnits = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = (u: AdUnitType) => {
      if (!q) return true;
      const slug = u.slug ?? "";
      const short = shortNameOf(slug, u.name).toLowerCase();
      const orig = (u.name ?? "").toLowerCase();
      const bucket = BUCKETS.find((b) => b.id === bucketOf(slug, u.name, (u as { adsExpressBucket?: string | null }).adsExpressBucket))?.label.toLowerCase() ?? "";
      return short.includes(q) || orig.includes(q) || bucket.includes(q) || "kill crew templates".includes(q);
    };
    const kcUnits: AdUnitType[] = [];
    const map = new Map<BucketId, AdUnitType[]>();
    BUCKETS.forEach((b) => map.set(b.id, []));
    staticAdUnits.forEach((u) => {
      if (!matchesSearch(u)) return;
      if (u.slug && KC_SLUGS.has(u.slug)) {
        kcUnits.push(u);
        return;
      }
      const b = bucketOf(u.slug, u.name, (u as { adsExpressBucket?: string | null }).adsExpressBucket);
      map.get(b)!.push(u);
    });
    if (kcUnits.length > 0) {
      const order = Array.from(KC_SLUGS);
      kcUnits.sort((a, b) => order.indexOf(a.slug ?? "") - order.indexOf(b.slug ?? ""));
    }
    const groups: Array<{ bucket: { id: KcBucketId; label: string; description: string }; units: AdUnitType[] }> = [];
    if (kcUnits.length > 0) groups.push({ bucket: KC_BUCKET, units: kcUnits });
    BUCKETS.forEach((b) => {
      const units = map.get(b.id) ?? [];
      if (units.length > 0) groups.push({ bucket: b, units });
    });
    return groups;
  }, [staticAdUnits, searchTerm]);

  const totalVisibleAdUnits = useMemo(
    () => groupedAdUnits.reduce((sum, g) => sum + g.units.length, 0),
    [groupedAdUnits],
  );

  // Default every bucket to collapsed once we know what buckets exist.
  // Re-runs only if a brand-new bucket appears (so user toggles persist).
  useEffect(() => {
    if (groupedAdUnits.length === 0) return;
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      let changed = false;
      if (!bucketsInitialized) {
        groupedAdUnits.forEach(({ bucket }) => {
          if (bucket.id === KC_BUCKET_ID) {
            openedBucketsRef.current.add(bucket.id);
            return;
          }
          next.add(bucket.id);
          changed = true;
        });
      } else {
        groupedAdUnits.forEach(({ bucket }) => {
          if (!prev.has(bucket.id) && !openedBucketsRef.current.has(bucket.id)) {
            next.add(bucket.id);
            changed = true;
          }
        });
      }
      return changed ? next : prev;
    });
    if (!bucketsInitialized) setBucketsInitialized(true);
  }, [groupedAdUnits, bucketsInitialized]);

  // Track buckets the user has explicitly opened so we don't re-collapse them.
  const openedBucketsRef = useRef<Set<string>>(new Set());

  const toggleBucket = useCallback((bucketId: string) => {
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucketId)) {
        next.delete(bucketId);
        openedBucketsRef.current.add(bucketId);
      } else {
        next.add(bucketId);
        openedBucketsRef.current.delete(bucketId);
      }
      return next;
    });
  }, []);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);

  // ─── Deep links (notifications) ──────────────────────────────────────
  // `?runId=` selects a specific run (wins over /active hydration below).
  const search = useSearch();
  const urlHydratedRef = useRef(false);
  useEffect(() => {
    if (urlHydratedRef.current) return;
    const params = new URLSearchParams(search);
    const runId = params.get("runId");
    if (!runId) return;
    urlHydratedRef.current = true;
    setActiveGenerationId(runId);
  }, [search]);

  // ─── Active-run hydration on mount/refresh ───────────────────────────
  // Pick up any in-flight (or recently-finished) run so navigating away
  // and back doesn't lose the user's results.
  const { data: activeRun } = useQuery<{ generation: ServerGeneration | null; ads: ServerGeneratedAd[] }>({
    queryKey: [`/api/brands/${brandId}/ad-generations/active`, "kc"],
    queryFn: () =>
      fetch(`/api/brands/${brandId}/ad-generations/active?variant=kc`, { credentials: "include" }).then((r) => r.json()),
    enabled: !!brandId && isKc && !activeGenerationId,
  });
  useEffect(() => {
    if (activeRun?.generation && !activeGenerationId) {
      setActiveGenerationId(activeRun.generation.id);
      if (activeRun.generation.productId) setProductId(activeRun.generation.productId);
      // Task #744 — restore the snapshotted variant on resume.
      const snap = (activeRun.generation as any).selectedVariantSnapshot;
      if (snap?.name) setSelectedVariantName(snap.name);
      if (activeRun.generation.selectedAdUnitIds) {
        setSelectedAdUnitIds(new Set(activeRun.generation.selectedAdUnitIds));
      }
    }
  }, [activeRun, activeGenerationId]);

  // ─── Generation polling ──────────────────────────────────────────────
  const { data: liveGen } = useQuery<{ generation: ServerGeneration; ads: ServerGeneratedAd[] }>({
    queryKey: ["/api/ad-generations", activeGenerationId],
    queryFn: async () => {
      const res = await fetch(`/api/ad-generations/${activeGenerationId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load generation (${res.status})`);
      return res.json();
    },
    enabled: !!activeGenerationId,
    refetchInterval: (q) => {
      const status = (q.state.data as { generation?: { status?: ServerGenerationStatus } } | undefined)?.generation?.status;
      if (!status) return 3000;
      return TERMINAL.includes(status) ? false : 3000;
    },
    refetchOnWindowFocus: true,
  });

  const isRunning = liveGen?.generation?.status === "queued" || liveGen?.generation?.status === "running";

  // Task #2117 — BRAND-LEVEL run-queue flag (master-managed, fail-closed).
  // ON: form stays usable during a run and further Generate clicks enqueue
  // additional runs (FIFO). OFF: today's locked-form behavior unchanged.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId && isKc,
  });
  const runQueueEnabled = betaFlags?.ads_express_run_queue === true;
  const formLocked = isRunning && !runQueueEnabled;
  const { data: runQueueData } = useAdsExpressRunQueue(brandId, "kc", isKc && runQueueEnabled);
  const queuedRuns = runQueueData?.runs ?? [];
  // Prefer the head of the queue (the actually-running run) when nothing is
  // selected yet.
  useEffect(() => {
    if (!runQueueEnabled || activeGenerationId || urlHydratedRef.current) return;
    if (queuedRuns.length > 0) setActiveGenerationId(queuedRuns[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQueueEnabled, activeGenerationId, queuedRuns.length]);

  // Single flat list of result tiles — cardinality is templates × 1.
  const resultTiles = useMemo(() => {
    if (!liveGen?.ads) return [];
    const adUnitById = new Map(staticAdUnits.map((u) => [u.id, u]));
    return liveGen.ads.map((ad) => ({
      ...ad,
      // Prefer the name snapshot captured at creation (survives template
      // renames/deletes); fall back to a live lookup for legacy rows.
      adUnitName:
        ad.adUnitNameSnapshot?.trim() ||
        (ad.adUnitTypeId ? (adUnitById.get(ad.adUnitTypeId)?.name ?? "Ad unit") : "Ad unit"),
    }));
  }, [liveGen?.ads, staticAdUnits]);

  const canGenerate = !!productId && selectedAdUnitIds.size > 0
    && selectedAdUnitIds.size <= 200 && !generating && !formLocked && simReady;

  // When the user picks a different product, default to the first
  // available reference image as the primary. They can add more by
  // clicking additional thumbnails.
  useEffect(() => {
    if (!product) {
      setSelectedProductImages([]);
      return;
    }
    const p = product as Product & { mediaJson?: string[] | null; adgenImageRefs?: string[] | null };
    const refs = Array.isArray(p.adgenImageRefs) ? p.adgenImageRefs.filter((u): u is string => typeof u === "string" && !!u) : [];
    const media = Array.isArray(p.mediaJson) ? p.mediaJson.filter((u): u is string => typeof u === "string" && !!u) : [];
    const seen = new Set<string>();
    let first: string | null = null;
    for (const u of [...refs, ...media]) {
      if (!seen.has(u)) { seen.add(u); if (first === null) first = u; }
    }
    setSelectedProductImages(first ? [first] : []);
  }, [product?.id]);

  // Once the results panel mounts after Generate, scroll the actual
  // scroll container (the layout's <main> element) so the user lands
  // on the in-progress tiles.
  useEffect(() => {
    if (!scrollToResultsRequested.current) return;
    if (resultTiles.length === 0 || !resultsRef.current) return;
    scrollToResultsRequested.current = false;
    const el = resultsRef.current;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [resultTiles.length]);

  const handleGenerate = async () => {
    if (!canGenerate || !brandId || !product) return;
    setGenerating(true);
    try {
      // Send in user-picked order — #1 is the main/front. [] is honored
      // as a text-only render by the server.
      const productImageUrls = selectedProductImages;
      const res = await apiRequest("POST", `/api/brands/${brandId}/ad-generations-kc`, {
        productId: product.id,
        // Task #744 — selected variant (null = no specific variant).
        selectedVariantName,
        adUnitTypeIds: Array.from(selectedAdUnitIds),
        aspectRatio,
        productImageUrls,
      });
      saveLastAspectRatio(aspectRatio);
      const data = (await res.json()) as { generation: { id: string; status?: string } };
      // Task #2117 — a submit that landed behind an existing run comes back
      // QUEUED: refresh the queue panel, tell the user, and stay on the run
      // they're already watching.
      if (data.generation.status === "queued" && activeGenerationId) {
        invalidateAdsExpressRunQueue(brandId, "kc");
        toast({
          title: "Run queued",
          description: "It will start automatically when the earlier run finishes.",
        });
      } else {
        setLocalOverrides({});
        // Mark intent to scroll; the effect below scrolls once the
        // results panel actually mounts (it only renders when tiles
        // exist, which happens on the next poll).
        scrollToResultsRequested.current = true;
        setActiveGenerationId(data.generation.id);
      }
    } catch (e: unknown) {
      // Server returns a friendly "missing KC design prompt" message
      // when any selected template lacks a prompt — show it as-is.
      toast({ title: "Couldn't start generation", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleCancel = async () => {
    if (!activeGenerationId || !brandId) return;
    try {
      await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${activeGenerationId}/cancel`, {});
    } catch (e: unknown) {
      toast({ title: "Cancel failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const setOverride = (id: string, patch: { imageUrl?: string | null; isSaved?: boolean; saving?: boolean; customName?: string | null }) =>
    setLocalOverrides((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));

  // Task #1798 — direct blob download via the authenticated image-download
  // proxy: never opens a new tab and always carries the resolved filename
  // (cross-origin `download` attributes are ignored by browsers).
  const downloadOne = async (url: string, name: string) => {
    const fileBase = sanitizeFilename(name);
    try {
      const proxyUrl = `/api/img-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(`${fileBase}.png`)}`;
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${fileBase}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    }
  };

  // Task #1494 — persist an accepted inline edit server-side so it survives
  // navigating away and back (works for saved AND unsaved ads). The optimistic
  // local override has already swapped the tile; this just makes it durable.
  const persistAcceptedEdit = useCallback(async (adId: string, newUrl: string) => {
    try {
      await apiRequest("POST", `/api/generated-ads/${adId}/accept-edit`, { imageUrl: newUrl });
      invalidateApprovalStates(queryClient, brandId);
      queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
      // Task #2040 — a persisted edit on a saved ad creates an undoable
      // previous version; refresh the undo index.
      invalidateUndoableAds(brandId);
    } catch (e: any) {
      toast({
        title: "Couldn't persist edit",
        description: e?.message ? String(e.message) : "The edit may not survive leaving this page.",
        variant: "destructive",
      });
      throw e;
    }
  }, [activeGenerationId, toast, brandId]);

  // Task #2040 — ads with an undoable last edit (saved tiles only).
  const undoableAdIds = useUndoableAdIds(brandId);
  const redoableAdIds = useRedoableAdIds(brandId);

  // Add/remove a tile from the "currently editing" set (drives the loading
  // overlays). Functional update so concurrent edits on different tiles don't
  // clobber each other.
  const markTileEditing = useCallback((tileId: string, editing: boolean) => {
    if (!editing) {
      setTileEditPhases((prev) => {
        if (!(tileId in prev)) return prev;
        const next = { ...prev };
        delete next[tileId];
        return next;
      });
    }
    setEditingTileIds((prev) => {
      if (editing === prev.has(tileId)) return prev;
      const next = new Set(prev);
      if (editing) next.add(tileId);
      else next.delete(tileId);
      return next;
    });
  }, []);

  // Tile ids with a poller actively running (dedupe — never two pollers for the
  // same tile), plus a mounted flag so a poller that's still looping when the
  // PAGE unmounts bails without clearing the persisted pending job (so it can be
  // re-attached on return) or writing into dead state.
  const pollingTilesRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mirror the open preview tile into a ref so a background poller can tell, at
  // completion time, whether the user is currently looking at this tile.
  const previewTileRef = useRef<typeof previewTile>(null);
  useEffect(() => {
    previewTileRef.current = previewTile;
  }, [previewTile]);

  // Poll a (possibly already-running) edit job to completion. The jobId is kept
  // in localStorage while in flight so that leaving this preview mid-edit
  // doesn't lose the result — returning re-attaches to the SAME job instead of
  // re-submitting (the edit runs server-side regardless), so there's no
  // duplicate billing.
  //
  // Crucially the poller is tied to the TILE, not the modal: it keeps running
  // after the dialog closes so the grid tile shows progress and updates itself
  // when the edit lands. On completion, if the modal is still open on this tile
  // the result is shown for review/accept; otherwise (background) it's
  // auto-applied to the tile so the preview pic refreshes.
  const pollEditJob = useCallback(
    async (jobId: string, tile: { id: string; imageUrl: string; name: string }) => {
      if (pollingTilesRef.current.has(tile.id)) return; // already polling this tile
      pollingTilesRef.current.add(tile.id);
      markTileEditing(tile.id, true);
      if (previewTileRef.current?.id === tile.id) setEditError(null);

      let outcome: { type: "ready"; url: string } | { type: "failed"; message: string };
      while (true) {
        if (!mountedRef.current) {
          pollingTilesRef.current.delete(tile.id);
          return; // page gone — leave pending for re-attach
        }
        try {
          const pollRes = await readImageEditStatus(jobId);
          if (!mountedRef.current) { pollingTilesRef.current.delete(tile.id); return; }
          if (pollRes.ok) {
            const status = (await pollRes.json()) as {
              status: "pending" | "ready" | "failed";
              image_url?: string;
              message?: string;
              phase?: string;
            };
            if (!mountedRef.current) { pollingTilesRef.current.delete(tile.id); return; }
            if (status.status === "pending") {
              setTileEditPhases((prev) =>
                prev[tile.id] === (status.phase ?? null) ? prev : { ...prev, [tile.id]: status.phase ?? null },
              );
            }
            if (status.status === "ready" && status.image_url) {
              outcome = { type: "ready", url: status.image_url };
              break;
            }
            if (status.status === "failed") {
              outcome = { type: "failed", message: status.message || "Edit failed" };
              break;
            }
          }
        } catch {
          if (mountedRef.current) setTileEditPhases(prev => ({ ...prev, [tile.id]: "checking" }));
        }
        await waitForImageEditCheck(jobId);
      }

      pollingTilesRef.current.delete(tile.id);
      if (!mountedRef.current) return; // page gone — leave pending for re-attach

      const openOnThisTile = previewTileRef.current?.id === tile.id;
      if (outcome.type === "ready") {
        markTileEditing(tile.id, false);
        updatePendingImageEdit(jobId, {
          resultImageUrl: outcome.url, delivery: openOnThisTile ? "review" : "background", error: undefined,
        });
        if (openOnThisTile) {
          // User is looking — surface the result for review/accept.
          setEditResultUrl(outcome.url);
        } else {
          // Background completion — auto-apply so the tile's preview pic updates,
          // and persist server-side so it survives navigation (Task #1494).
          try {
            await persistPendingImageEdit(jobId, () => persistAcceptedEdit(tile.id, outcome.url));
            if (mountedRef.current) setLocalOverrides((prev) => ({ ...prev, [tile.id]: { ...(prev[tile.id] ?? {}), imageUrl: outcome.url } }));
            toast({ title: "Edit applied", description: tile.name });
          } catch (e: any) {
            updatePendingImageEdit(jobId, { delivery: "review", error: e?.message || "Couldn't save edit" });
            if (mountedRef.current) setTileEditErrors(prev => ({ ...prev, [tile.id]: "Edit complete — awaiting save retry. Reopen to save." }));
          }
        }
      } else if (outcome.type === "failed") {
        setTileEditErrors((prev) => ({ ...prev, [tile.id]: outcome.message }));
        updatePendingImageEdit(jobId, { delivery: "review", error: outcome.message });
        markTileEditing(tile.id, false);
        if (openOnThisTile) setEditError(outcome.message);
        else toast({ title: "Edit failed", description: outcome.message, variant: "destructive" });
      }
    },
    [markTileEditing, persistAcceptedEdit],
  );

  const handleEditSubmit = async () => {
    if (!previewTile || !editPrompt.trim() || editingTileIds.has(previewTile.id)) return;
    const existing = findPendingImageEdit(previewTile.imageUrl);
    if (existing && !existing.error && !editResultUrl) {
      void pollEditJob(existing.jobId, previewTile);
      return;
    }
    const tile = previewTile;
    setTileEditErrors((prev) => { const next = { ...prev }; delete next[tile.id]; return next; });
    markTileEditing(tile.id, true);
    setEditError(null);
    const sourceUrl = editResultUrl ?? tile.imageUrl;
    try {
      // Submit the edit as a background job. A slow/busy Fal image queue keeps
      // working in the background instead of timing out; the edited image
      // appears when ready. Real failures still surface a precise message.
      const res = await fetch("/api/edit-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          image_url: sourceUrl,
          prompt: editPrompt.trim(),
          // The tile IS the generated ad — its id lets the server resolve the
          // brand-logo context for the post-edit wrong-logo QA check.
          adId: tile.id,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || `Edit failed (${res.status})`);
      }
      const { jobId } = (await res.json()) as { jobId?: string };
      if (!jobId) throw new Error("Edit failed");

      // Persist the in-flight job keyed on this tile's image so leaving the
      // preview mid-edit doesn't lose the result — returning re-attaches.
      rememberPendingImageEdit(tile.imageUrl, jobId);
      // Fire-and-forget: the poller runs in the background so closing the modal
      // doesn't stop it (the tile keeps its loading overlay and updates on done).
      void pollEditJob(jobId, tile);
    } catch (e: unknown) {
      markTileEditing(tile.id, false);
      setEditError(e instanceof Error ? e.message : "Edit failed");
    }
  };

  const handleEditAccept = async () => {
    if (!previewTile || !editResultUrl) return;
    const tile = previewTile;
    const url = editResultUrl;
    const pending = findPendingImageEdit(tile.imageUrl);
    try {
      if (pending) await persistPendingImageEdit(pending.jobId, () => persistAcceptedEdit(tile.id, url));
      else await persistAcceptedEdit(tile.id, url);
    } catch (e: any) {
      if (pending) updatePendingImageEdit(pending.jobId, { delivery: "review", resultImageUrl: url, error: e?.message || "Couldn't save edit" });
      if (previewTileRef.current?.id === tile.id) setEditError("Edit complete — awaiting save retry. Please retry saving.");
      return;
    }
    if (!mountedRef.current) return;
    setOverride(tile.id, { imageUrl: url });
    toast({ title: "Edit applied" });
    if (previewTileRef.current?.id !== tile.id) return;
    setPreviewTile(null);
    setEditPrompt("");
    setEditResultUrl(null);
    setEditError(null);
  };

  // Re-attach background pollers for any result tile that has an edit still in
  // flight (modal closed mid-edit, or page refreshed / navigated back). This
  // makes the loading overlay reappear on the grid tile and resumes the SAME
  // job — the result auto-applies when ready, with no re-submit (no double
  // billing). Deduped via pollingTilesRef so it never starts a second poller.
  useEffect(() => {
    for (const ad of resultTiles) {
      const o = localOverrides[ad.id] ?? {};
      const imageUrl = o.imageUrl ?? ad.imageUrlSaved ?? ad.imageUrlOriginal;
      if (!imageUrl) continue;
      const pending = findPendingImageEdit(imageUrl);
      if (pending && !pending.resultImageUrl && !pending.error && pending.delivery !== "review" && !pollingTilesRef.current.has(ad.id)) {
        void pollEditJob(pending.jobId, { id: ad.id, imageUrl, name: ad.adUnitName });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultTiles, localOverrides, pollEditJob, imageEditsRevision]);

  useEffect(() => {
    if (!previewTile) return;
    const pending = findPendingImageEdit(previewTile.imageUrl);
    if (pending?.resultImageUrl) {
      if (pending.delivery !== "review") updatePendingImageEdit(pending.jobId, { delivery: "review" });
      setEditResultUrl(pending.resultImageUrl);
      setEditError(pending.error ? "Edit complete — awaiting save retry. Please retry saving." : null);
    } else if (pending?.error) setEditError(pending.error);
  }, [previewTile, imageEditsRevision]);

  const handleRetry = async (adId: string) => {
    try {
      await apiRequest("PATCH", `/api/generated-ads/${adId}`, { status: "pending" });
      if (activeGenerationId && brandId) {
        await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${activeGenerationId}/start`, {});
      }
    } catch (e: unknown) {
      toast({ title: "Retry failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  // Task #2439 — master-only "Regenerate with fresh prompt". Unlike the
  // regen above (same stored prompt, new roll), the server clears the ad's
  // prompt + Fal request id so the worker re-runs the Sim prompt workflow
  // with the run's original snapshotted inputs, then renders anew. The
  // server enforces the master gate; the button is simply hidden otherwise.
  const isMaster = user?.isMaster === true;
  const handleFreshRegen = async (adId: string) => {
    try {
      await apiRequest("POST", `/api/generated-ads/${adId}/fresh-prompt-regen`, {});
      queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
      toast({
        title: "Regenerating with a fresh prompt",
        description: "A new prompt is being written from this ad's original run inputs — the tile will update when the new render lands.",
      });
    } catch (e: unknown) {
      toast({ title: "Fresh-prompt regenerate failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  // Rename a finished tile (mirrors ads-express.tsx). Optimistic override so
  // the title updates immediately; PATCH persists it into My Ads.
  const handleRename = async (ad: ServerGeneratedAd & { adUnitName: string }, name: string | null) => {
    const prev = localOverrides[ad.id]?.customName !== undefined
      ? localOverrides[ad.id]!.customName ?? null
      : ad.customName ?? null;
    setOverride(ad.id, { customName: name });
    setPreviewTile((pt) => (pt?.id === ad.id ? { ...pt, name: name?.trim() || ad.adUnitName } : pt));
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, { customName: name });
      await queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
    } catch (e: unknown) {
      setOverride(ad.id, { customName: prev });
      toast({ title: "Couldn't rename", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const handleSave = async (ad: ServerGeneratedAd) => {
    const o = localOverrides[ad.id] ?? {};
    if ((o.isSaved ?? ad.isSaved) || o.saving) return;
    if (ad.status !== "ready" || !ad.imageUrlOriginal) return;
    setOverride(ad.id, { saving: true });
    try {
      await apiRequest("POST", `/api/generated-ads/${ad.id}/save`, {});
      setOverride(ad.id, { isSaved: true, saving: false });
      // Task #2040 — an ad edited before saving becomes undoable at save.
      invalidateUndoableAds(brandId);
      invalidateGeneratedAdListings();
      toast({ title: "Saved to library" });
    } catch (e: unknown) {
      setOverride(ad.id, { saving: false });
      toast({ title: "Save failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  // Task #2161 — "Copy as is" / "Copy & reframe" on saved tiles. Both
  // create an independent saved duplicate in My Ads (own R2 object); the
  // original tile/row stays exactly as it was.
  const [copyingAdIds, setCopyingAdIds] = useState<Set<string>>(new Set());
  const handleCopyAd = async (adId: string, ratio: AspectRatio | null) => {
    setCopyingAdIds((prev) => new Set(prev).add(adId));
    try {
      if (ratio) {
        await copyAndReframeAd(adId, ratio);
        toast({
          title: `Copy saved at ${ratio}`,
          description: "The original is untouched — both sizes are now in My Ads.",
        });
      } else {
        await duplicateAdAsIs(adId);
        toast({
          title: "Copy saved to My Ads",
          description: "An independent duplicate was added to your library.",
        });
      }
      // Refresh the My Ads grids so the copy shows up immediately.
      invalidateGeneratedAdListings();
    } catch (e: unknown) {
      toast({ title: "Copy failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setCopyingAdIds((prev) => {
        const next = new Set(prev);
        next.delete(adId);
        return next;
      });
    }
  };

  const toggleWinner = async (ad: ServerGeneratedAd) => {
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, { isWinner: !ad.isWinner });
      await queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
      toast({ title: ad.isWinner ? "Winner removed" : "Marked as winner" });
    } catch (e: unknown) {
      toast({ title: "Couldn't update", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const toggleArchive = async (ad: ServerGeneratedAd) => {
    try {
      await apiRequest("POST", `/api/generated-ads/${ad.id}/${ad.archivedAt ? "unarchive" : "archive"}`, {});
      await queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
      toast({ title: ad.archivedAt ? "Restored" : "Archived" });
    } catch (e: unknown) {
      toast({ title: "Couldn't update", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const editTags = async (ad: ServerGeneratedAd) => {
    const current = (ad.tags ?? []).join(", ");
    const next = window.prompt("Comma-separated tags:", current);
    if (next == null) return;
    const tags = next.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 40);
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, { tags });
      await queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
      toast({ title: "Tags updated" });
    } catch (e: unknown) {
      toast({ title: "Couldn't update tags", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const toggleAdUnit = (id: string) => {
    setSelectedAdUnitIds((prev) => {
      const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
    });
  };

  // ─── Render ─────────────────────────────────────────────────────────
  if (loadingBrand) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (brandError || !brand || !isKc) {
    return null;
  }

  // Resolve every reference image from the selected product (adgenRefs
  // first, then any extra media not already covered) so the user can
  // pick which ones to send to Fal.
  let availableProductImages: string[] = [];
  let productTags: string[] = [];
  let productDescription: string | null = null;
  if (product) {
    const p = product as Product & { description?: string | null; tags?: string[] | null; mediaJson?: string[] | null; adgenImageRefs?: string[] | null };
    const refs = Array.isArray(p.adgenImageRefs) ? p.adgenImageRefs.filter((u): u is string => typeof u === "string" && !!u) : [];
    const media = Array.isArray(p.mediaJson) ? p.mediaJson.filter((u): u is string => typeof u === "string" && !!u) : [];
    const seen = new Set<string>();
    for (const u of [...refs, ...media]) {
      if (seen.has(u)) continue;
      seen.add(u);
      availableProductImages.push(u);
    }
    productTags = p.tags ?? [];
    productDescription = p.description ?? null;
  }
  const productImage = availableProductImages[0] ?? null;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      <header className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 via-background to-background p-5 sm:p-6">
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="space-y-1.5 min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              <Shirt className="h-3 w-3" />
              <span data-testid="text-kc-tag">Kill Crew</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="text-page-title">
              KillCrew Ad Studio
            </h1>
            <p className="text-sm text-muted-foreground">
              Pick a product, choose templates, then generate. Steer each render from the preview.
            </p>
          </div>
        </div>
      </header>

      {!simReady && (
        <Card className="p-4 border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20" data-testid="banner-not-configured">
          <div className="flex items-start gap-3">
            <Wand2 className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm space-y-1">
              <div className="font-medium">Generation isn't fully configured yet.</div>
              <div className="text-xs text-muted-foreground">
                The AI workflow is missing required configuration. Ask an admin to finish setup.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Single combined panel: product + direction + templates */}
      <Card className="relative overflow-hidden p-0 border-primary/20 shadow-sm ring-1 ring-primary/5">
        {/* Decorative gradient accents */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative p-5 sm:p-6 space-y-5">
          {/* Section header — clickable to toggle */}
          <button
            type="button"
            onClick={() => setProductSectionOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 flex-wrap text-left rounded-md -m-1 p-1 hover:bg-primary/[0.03] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-expanded={productSectionOpen}
            data-testid="button-toggle-product-section"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20 shrink-0">
                <Package className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold leading-tight" data-testid="text-section-product">Choose product</h2>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/80">Step 1</span>
                </div>
                {productId && product ? (
                  <div className="flex items-center gap-2 mt-1 min-w-0">
                    {productImage ? (
                      <img
                        src={thumbUrl(productImage, 64)!}
                        onError={thumbFallback(productImage)}
                        loading="lazy"
                        alt={product.name}
                        className="h-5 w-5 rounded object-cover border shrink-0"
                        data-testid="img-product-summary"
                      />
                    ) : (
                      <div className="h-5 w-5 rounded border bg-muted flex items-center justify-center shrink-0">
                        <Package className="h-3 w-3 text-muted-foreground" />
                      </div>
                    )}
                    <span className="text-xs text-foreground/80 truncate" data-testid="text-product-summary">{product.name}</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground leading-tight mt-0.5">No product selected — click to choose one.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {productId && product && availableProductImages.length > 0 && (
                <div className="text-[11px] text-muted-foreground tabular-nums" data-testid="text-refs-count">
                  <span className="font-semibold text-foreground">{selectedProductImages.length}</span>/{availableProductImages.length} ref{availableProductImages.length === 1 ? "" : "s"}
                </div>
              )}
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${productSectionOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </div>
          </button>

          {productSectionOpen && (
          <>
          {/* Product dropdown */}
          <SearchableSelect
            value={productId}
            onValueChange={(v) => { setProductId(v); setSelectedVariantName(null); }}
            placeholder="Select a product…"
            testId="select-product"
            triggerClassName="h-12 max-w-xl bg-background hover:bg-muted/40 transition-colors border-input"
            options={products.map((p) => {
              const pp = p as Product & { mediaJson?: string[] | null; adgenImageRefs?: string[] | null };
              const media = Array.isArray(pp.mediaJson) ? pp.mediaJson : [];
              const refs = Array.isArray(pp.adgenImageRefs) ? pp.adgenImageRefs : [];
              const thumb = (media.find((u) => typeof u === "string" && u) ?? refs.find((u) => typeof u === "string" && u)) || null;
              return {
                value: p.id,
                label: p.name,
                testId: `option-product-${p.id}`,
                node: (
                  <div className="flex items-center gap-2">
                    {thumb ? (
                      <img
                        src={thumbUrl(thumb, 64)!}
                        onError={thumbFallback(thumb)}
                        loading="lazy"
                        alt={p.name}
                        className="h-7 w-7 rounded object-cover border shrink-0"
                        data-testid={`img-option-product-${p.id}`}
                      />
                    ) : (
                      <div
                        className="h-7 w-7 rounded border bg-muted flex items-center justify-center shrink-0"
                        data-testid={`img-option-product-${p.id}`}
                      >
                        <Package className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    )}
                    <span className="truncate">{p.name}</span>
                  </div>
                ),
              };
            })}
          />

          {/* Task #744 — optional variant picker (renders only when the product has variants). */}
          {productId && product && (
            <VariantPicker
              product={product}
              value={selectedVariantName}
              onChange={setSelectedVariantName}
              testIdPrefix="kc"
            />
          )}

          {productId && product && (
            <div className="space-y-4">
              {/* Polished product preview card */}
              <div
                className="relative flex flex-col sm:flex-row gap-4 rounded-lg border border-primary/15 bg-gradient-to-br from-primary/[0.04] via-background to-background p-4 sm:p-5 shadow-sm"
                data-testid="card-product-preview"
              >
                <div className="relative h-32 w-32 sm:h-36 sm:w-36 shrink-0 mx-auto sm:mx-0">
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary/15 to-primary/0 blur-md" aria-hidden="true" />
                  <div className="relative h-full w-full rounded-lg border bg-background overflow-hidden flex items-center justify-center shadow-sm">
                    {productImage ? (
                      <img
                        src={thumbUrl(productImage, 320)!}
                        onError={thumbFallback(productImage)}
                        loading="lazy"
                        alt={product.name}
                        className="h-full w-full object-cover"
                        data-testid="img-product-preview"
                      />
                    ) : (
                      <Package className="h-10 w-10 text-muted-foreground/40" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="text-base sm:text-lg font-semibold leading-tight tracking-tight" data-testid="text-product-name">
                    {product.name}
                  </div>
                  {productDescription && (
                    <p className="text-xs sm:text-[13px] text-muted-foreground leading-relaxed line-clamp-3" data-testid="text-product-description">
                      {productDescription}
                    </p>
                  )}
                  {productTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {productTags.slice(0, 6).map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="text-[10px] py-0 px-1.5 font-medium border-primary/20 bg-primary/5 text-foreground/80"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {availableProductImages.length > 1 && (
                <div className="space-y-2.5" data-testid="panel-product-images">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-xs font-semibold text-foreground/80">
                      Reference images
                      <span className="ml-1.5 font-normal text-muted-foreground">click in order — #1 is the main shot</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {availableProductImages.map((url) => {
                      const order = selectedProductImages.indexOf(url);
                      const checked = order >= 0;
                      const isMain = order === 0;
                      return (
                        <button
                          key={url}
                          type="button"
                          onClick={() => {
                            setSelectedProductImages((prev) => {
                              const idx = prev.indexOf(url);
                              if (idx >= 0) return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
                              return [...prev, url];
                            });
                          }}
                          className={`group relative h-20 w-20 rounded-lg border overflow-hidden bg-background transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                            checked
                              ? isMain
                                ? "ring-2 ring-primary border-primary shadow-md shadow-primary/20 scale-[1.02]"
                                : "ring-2 ring-primary/60 border-primary/60 shadow-sm"
                              : "opacity-70 hover:opacity-100 hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5"
                          }`}
                          aria-pressed={checked}
                          data-testid={`button-pick-image-${url}`}
                          title={checked ? (isMain ? "Main reference (#1) — click to remove" : `Reference #${order + 1} — click to remove`) : "Add as reference (click order matters)"}
                        >
                          <img src={thumbUrl(url, 160)!} onError={thumbFallback(url)} loading="lazy" alt="Product reference" className="h-full w-full object-cover" />
                          {checked && (
                            <div className={`absolute top-1 right-1 rounded-full h-5 min-w-5 px-1 flex items-center justify-center shadow text-[11px] font-bold tabular-nums ${isMain ? "bg-primary text-primary-foreground" : "bg-background text-foreground border border-primary/60"}`}>
                              {order + 1}
                            </div>
                          )}
                          {!checked && (
                            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {selectedProductImages.length === 0 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      No images selected — the AI will render from text only.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </Card>

      {/* Templates */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">Pick ad formats</h2>
            <span className="text-xs text-muted-foreground" data-testid="text-selected-count">
              {selectedAdUnitIds.size} of {staticAdUnits.length} selected
            </span>
          </div>
          <div className="flex items-end gap-3">
            <Select value={aspectRatio} onValueChange={(v) => setAspectRatio(normalizeAspectRatio(v))}>
              <SelectTrigger className="w-28" data-testid="select-aspect-ratio">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AD_ASPECT_RATIO_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="pl-9 h-9 rounded-full text-xs"
                disabled={formLocked}
                data-testid="input-search-templates"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-semibold mr-1">
            Presets
          </span>
          {PRESETS.map((preset) => {
            const isActive = activePreset === preset.id;
            return (
              <Button
                key={preset.id}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => applyPreset(preset.id)}
                disabled={formLocked}
                className="rounded-full text-xs h-8"
                data-testid={`button-preset-${preset.id}`}
              >
                {addMode ? <Plus className="h-3 w-3 mr-1" /> : isActive ? <Check className="h-3 w-3 mr-1" /> : null}
                {preset.label}
              </Button>
            );
          })}
          <div className="flex items-center gap-2 ml-auto">
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={addMode}
                    aria-label="Toggle add mode"
                    onClick={() => setAddMode((v) => !v)}
                    disabled={formLocked}
                    className={[
                      "flex items-center gap-2 rounded-full px-3 h-8 border transition-colors",
                      addMode ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground",
                      formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                    ].join(" ")}
                    data-testid="switch-add-mode"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">Add mode</span>
                    <span
                      aria-hidden="true"
                      className={[
                        "relative inline-block w-7 h-4 rounded-full transition-colors",
                        addMode ? "bg-primary" : "bg-muted-foreground/30",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "absolute top-0.5 h-3 w-3 rounded-full bg-background shadow transition-transform",
                          addMode ? "translate-x-3.5" : "translate-x-0.5",
                        ].join(" ")}
                      />
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-xs">
                  {addMode
                    ? "Presets ADD to your current selection"
                    : "Presets REPLACE your current selection"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedAdUnitIds(new Set(staticAdUnits.map((u) => u.id)))}
              disabled={
                formLocked ||
                staticAdUnits.length === 0 ||
                selectedAdUnitIds.size === staticAdUnits.length
              }
              data-testid="button-select-all"
              className="rounded-full text-xs h-8"
            >
              Select all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllAdUnits}
              disabled={formLocked || selectedAdUnitIds.size === 0}
              data-testid="button-clear-all"
              className="rounded-full text-xs h-8"
            >
              Clear
            </Button>
          </div>
        </div>

        <div
          className={[
            "rounded-md border px-3 py-2 text-[11px] flex items-center gap-2",
            addMode
              ? "border-primary/40 bg-primary/5 text-primary"
              : "border-border/50 bg-muted/30 text-muted-foreground",
          ].join(" ")}
          data-testid="banner-mode"
        >
          {addMode ? (
            <>
              <Plus className="h-3 w-3" />
              <span>
                <strong>Add mode on.</strong> Clicking a preset adds its templates to your current selection.
              </span>
            </>
          ) : (
            <>
              <RotateCcw className="h-3 w-3" />
              <span>
                <strong>Replace mode.</strong> Clicking a preset replaces your current selection. Toggle Add mode to stack presets.
              </span>
            </>
          )}
        </div>

        {staticAdUnits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No static ad units available for this brand.</p>
        ) : totalVisibleAdUnits === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="text-no-results">
            <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No templates match "{searchTerm}".</p>
          </div>
        ) : (
          <div className="space-y-7">
            {groupedAdUnits.map(({ bucket, units }) => {
              const visual = BUCKET_VISUALS[bucket.id];
              const isCollapsed = collapsedBuckets.has(bucket.id);
              const selectedInBucket = units.reduce((n, u) => n + (selectedAdUnitIds.has(u.id) ? 1 : 0), 0);
              return (
                <div key={bucket.id} data-testid={`section-bucket-${bucket.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleBucket(bucket.id)}
                    className="w-full flex items-center justify-between gap-3 mb-3 text-left rounded-md -m-1 p-1 hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    aria-expanded={!isCollapsed}
                    data-testid={`button-toggle-bucket-${bucket.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={["h-9 w-9 rounded-lg flex items-center justify-center shrink-0", visual.accent].join(" ")}>
                        {visual.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-bold tracking-tight" data-testid={`text-bucket-label-${bucket.id}`}>
                            {bucket.label}
                          </h3>
                          <Badge
                            variant="secondary"
                            className="h-5 px-1.5 text-[10px] font-bold tabular-nums"
                            data-testid={`badge-bucket-count-${bucket.id}`}
                          >
                            {units.length}
                          </Badge>
                          {selectedInBucket > 0 && (
                            <Badge
                              className="h-5 px-1.5 text-[10px] font-bold tabular-nums bg-primary text-primary-foreground"
                              data-testid={`badge-bucket-selected-${bucket.id}`}
                            >
                              {selectedInBucket} picked
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-1">
                          {bucket.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className={["hidden sm:block h-px w-16 opacity-30", visual.bar].join(" ")} />
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`}
                        aria-hidden="true"
                      />
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {units.map((u) => (
                        <TemplateCard
                          key={u.id}
                          unit={u}
                          checked={selectedAdUnitIds.has(u.id)}
                          disabled={formLocked}
                          onToggle={() => toggleAdUnit(u.id)}
                          isAdmin={isAdmin}
                          hasPrompt={(promptByUnitId.get(u.id)?.designPrompt ?? "").trim().length > 0}
                          onEditPrompt={() => openPromptEditor(u)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Sticky generate bar */}
      {/* Task #2117 — run-queue panel (flag-gated): Running + Queued runs
          with per-run View/Cancel. */}
      {runQueueEnabled && brandId && queuedRuns.length > 0 && (
        <AdsExpressRunQueuePanel
          brandId={brandId}
          variant="kc"
          runs={queuedRuns}
          activeGenerationId={activeGenerationId}
          onSelect={(id) => {
            setLocalOverrides({});
            setActiveGenerationId(id);
            urlHydratedRef.current = true;
          }}
          className="mb-4"
        />
      )}

      <div className="sticky bottom-0 bg-background/90 backdrop-blur border-t -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {product ? <><span className="font-medium">{product.name}</span> · </> : null}
          <span>{selectedAdUnitIds.size} template{selectedAdUnitIds.size === 1 ? "" : "s"}</span>
          <span className="ml-2 text-xs">= <span className={selectedAdUnitIds.size > 200 ? "text-destructive font-medium" : "font-medium"} data-testid="text-fanout">{selectedAdUnitIds.size}</span> ad{selectedAdUnitIds.size === 1 ? "" : "s"}{selectedAdUnitIds.size > 200 ? " (max 200)" : ""}</span>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <Button variant="outline" size="sm" onClick={handleCancel} data-testid="button-cancel">
              Cancel
            </Button>
          )}
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="button-generate"
          >
            {generating || formLocked ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
            Generate
          </Button>
        </div>
      </div>

      {/* Results */}
      {resultTiles.length > 0 && (
        <Card ref={resultsRef} className="p-5 space-y-4 scroll-mt-4" data-testid="panel-results">
          <h2 className="font-semibold flex items-center gap-2">
            <ImageIcon className="h-4 w-4" /> Results
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {resultTiles.map((ad) => {
              const o = localOverrides[ad.id] ?? {};
              const customName = o.customName !== undefined ? o.customName : (ad.customName ?? null);
              const displayName = customName?.trim() || ad.adUnitName;
              const imageUrl = o.imageUrl ?? ad.imageUrlSaved ?? ad.imageUrlOriginal;
              const isSaved = o.isSaved ?? ad.isSaved;
              const saving = o.saving ?? false;
              const status = ad.status;
              const tags = ad.tags ?? [];
              return (
                <div key={ad.id} className="rounded-lg border overflow-hidden bg-card relative" data-testid={`tile-${ad.id}`}>
                  {ad.isWinner && (
                    <Badge className="absolute top-2 left-2 z-10" data-testid={`badge-winner-${ad.id}`}>
                      <Trophy className="h-3 w-3 mr-1" /> Winner
                    </Badge>
                  )}
                  <div className="relative aspect-[4/5] bg-muted/40 flex items-center justify-center">
                    {editingTileIds.has(ad.id) && (
                      <div
                        className="absolute inset-0 z-10 bg-black/50 flex flex-col items-center justify-center gap-1"
                        data-testid={`editing-${ad.id}`}
                      >
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                        <span className="text-[11px] font-medium text-white">
                          {imageEditPhaseLabel(tileEditPhases[ad.id] ?? null)}
                        </span>
                      </div>
                    )}
                    {imageUrl ? (
                      <button
                        type="button"
                        onClick={() => setPreviewTile({ id: ad.id, imageUrl: imageUrl!, name: displayName, templateName: ad.adUnitName })}
                        className="w-full h-full block cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid={`button-preview-${ad.id}`}
                        title="Click to preview"
                      >
                        <AdImage src={imageUrl} thumbWidth={640} alt={displayName} className="w-full h-full object-cover" data-testid={`img-${ad.id}`} />
                      </button>
                    ) : status === "failed" ? (
                      <XCircle className="h-8 w-8 text-destructive" />
                    ) : status === "cancelled" ? (
                      <XCircle className="h-8 w-8 text-muted-foreground" />
                    ) : (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="text-xs font-medium" title={displayName}>
                      {status === "ready" ? (
                        <InlineRenameTitle
                          customName={customName}
                          fallbackName={ad.adUnitName}
                          onSave={(name) => handleRename(ad, name)}
                          className="[&>span:first-child]:truncate"
                          inputClassName="text-xs font-medium"
                          testId={`tile-name-${ad.id}`}
                        />
                      ) : (
                        <span className="block truncate">{displayName}</span>
                      )}
                      {!!customName?.trim() && customName.trim() !== ad.adUnitName && (
                        <span
                          className="mt-0.5 block text-[10px] font-normal text-muted-foreground truncate"
                          title={`Template: ${ad.adUnitName}`}
                          data-testid={`text-template-${ad.id}`}
                        >
                          Template: {ad.adUnitName}
                        </span>
                      )}
                    </div>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1" data-testid={`tags-${ad.id}`}>
                        {tags.slice(0, 3).map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px] py-0">{t}</Badge>
                        ))}
                      </div>
                    )}
                    {tileEditErrors[ad.id] && <div className="text-xs text-destructive" role="alert" data-testid="text-image-edit-failure">{tileEditErrors[ad.id]}</div>}
                    {ad.errorMessage && <div className="text-[11px] text-destructive line-clamp-2">{ad.errorMessage}</div>}
                    <div className="flex items-center gap-1 flex-wrap">
                      {status === "ready" && imageUrl && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSave(ad)}
                            disabled={isSaved || saving}
                            data-testid={`button-save-${ad.id}`}
                            title={isSaved ? "Saved" : "Save to library"}
                          >
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : isSaved ? <BookmarkCheck className="h-3.5 w-3.5" />
                              : <Bookmark className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPreviewTile({ id: ad.id, imageUrl: imageUrl!, name: displayName, templateName: ad.adUnitName })}
                            data-testid={`button-edit-${ad.id}`}
                            title="Edit / preview"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetry(ad.id)}
                            data-testid={`button-regen-${ad.id}`}
                            title="Regenerate with the same prompt"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                          {/* Task #2439 — master-only fresh-prompt regenerate
                              (server enforces the real gate). */}
                          {isMaster && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleFreshRegen(ad.id)}
                              data-testid={`button-fresh-regen-${ad.id}`}
                              title="Regenerate with a FRESH prompt — re-runs the prompt workflow with this ad's original run inputs (new creative), unlike Regenerate which reuses the stored prompt"
                            >
                              <Wand2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleArchive(ad)}
                            data-testid={`button-archive-${ad.id}`}
                            title={ad.archivedAt ? "Restore" : "Archive"}
                          >
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => imageUrl && downloadOne(imageUrl, displayName)}
                            data-testid={`button-download-${ad.id}`}
                            title="Download"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {/* Task #2161 — saved tiles get a copy menu: exact
                              duplicate or duplicate reframed to a new ratio.
                              Both land in My Ads; the original is untouched. */}
                          {isSaved && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={copyingAdIds.has(ad.id)}
                                  data-testid={`button-copy-${ad.id}`}
                                  title="Duplicate this saved ad into My Ads"
                                >
                                  {copyingAdIds.has(ad.id) ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <CopyPlus className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem
                                  onSelect={() => handleCopyAd(ad.id, null)}
                                  data-testid={`menu-copy-as-is-${ad.id}`}
                                >
                                  Copy as is
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Task #2213 — hide the ad's CURRENT ratio: prefer
                                    the viewed generation's ratio (the ads were
                                    rendered at it) over the form's dropdown, which
                                    the user may have changed since the run. The
                                    server also short-circuits same-ratio reframes
                                    to a lossless copy as a backstop. */}
                                {(["1:1", "4:5", "9:16"] as const)
                                  .filter((r) => r !== (liveGen?.generation?.aspectRatio ?? aspectRatio))
                                  .map((r) => (
                                    <DropdownMenuItem
                                      key={r}
                                      onSelect={() => handleCopyAd(ad.id, r)}
                                      data-testid={`menu-copy-reframe-${ad.id}-${r.replace(":", "x")}`}
                                    >
                                      Copy &amp; reframe to {r}
                                    </DropdownMenuItem>
                                  ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {brandId && (
                            <>
                              <UndoAdEditButton
                                adId={ad.id}
                                brandId={brandId}
                                undoableAdIds={undoableAdIds}
                                onUndone={(url) => {
                                  setLocalOverrides((prev) => ({ ...prev, [ad.id]: { ...(prev[ad.id] ?? {}), imageUrl: url } }));
                                  queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
                                }}
                                className="h-8 text-xs"
                              />
                              <RedoAdEditButton
                                adId={ad.id}
                                brandId={brandId}
                                redoableAdIds={redoableAdIds}
                                onRedone={(url) => {
                                  setLocalOverrides((prev) => ({ ...prev, [ad.id]: { ...(prev[ad.id] ?? {}), imageUrl: url } }));
                                  queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", activeGenerationId] });
                                }}
                                className="h-8 text-xs"
                              />
                            </>
                          )}
                        </>
                      )}
                      {(status === "failed" || status === "cancelled") && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleRetry(ad.id)}
                          data-testid={`button-retry-${ad.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                        </Button>
                      )}
                      {(status === "failed" || status === "cancelled") && isMaster && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleFreshRegen(ad.id)}
                          data-testid={`button-fresh-regen-failed-${ad.id}`}
                          title="Regenerate with a FRESH prompt — re-runs the prompt workflow with this ad's original run inputs (new creative)"
                        >
                          <Wand2 className="h-3.5 w-3.5 mr-1" /> Fresh prompt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <AdsExpressPreviousSessions
        brandId={brandId!}
        variant="kc"
        excludeGenerationId={activeGenerationId}
        adUnits={adUnits}
      />

      {/* Large preview */}
      <Dialog
        open={!!previewTile}
        onOpenChange={(v) => {
          if (!v) {
            setPreviewTile(null);
            setEditPrompt("");
            setEditError(null);
            setEditResultUrl(null);
            // NOTE: an in-flight edit is NOT cancelled here — its poller keeps
            // running in the background, the grid tile shows "Editing…", and the
            // result auto-applies to the tile when it lands.
          }
        }}
      >
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black/95 border-0" data-testid="dialog-preview-ad">
          <DialogHeader className="sr-only">
            <DialogTitle>{previewTile?.name ?? "Ad preview"}</DialogTitle>
            <DialogDescription>Full-size preview and inline edit of the generated ad image.</DialogDescription>
          </DialogHeader>
          {previewTile && (
            <div className="flex flex-col">
              <div className="relative flex items-center justify-center max-h-[70vh] overflow-auto p-4">
                <AdImage
                  src={editResultUrl ?? previewTile.imageUrl}
                  alt={previewTile.name}
                  className="max-w-full max-h-[68vh] object-contain"
                  data-testid="img-preview-large"
                />
                {editingTileIds.has(previewTile.id) && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-white" />
                      <span className="text-sm text-white font-medium">
                        {imageEditPhaseLabel(tileEditPhases[previewTile.id] ?? null)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-4 py-3 bg-background border-t space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" title={previewTile.name}>{previewTile.name}</div>
                    {previewTile.templateName && previewTile.templateName !== previewTile.name && (
                      <div className="text-[11px] text-muted-foreground truncate" data-testid="text-preview-template">
                        Template: {previewTile.templateName}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadOne(editResultUrl ?? previewTile.imageUrl, previewTile.name)}
                    data-testid="button-preview-download"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                  </Button>
                </div>

                {!editResultUrl && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-baseline justify-between">
                      <label htmlFor="input-edit-prompt" className="text-xs font-medium text-muted-foreground">
                        Creative direction
                      </label>
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        {editPrompt.length}/2000
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        id="input-edit-prompt"
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value.slice(0, 2000))}
                        placeholder='Describe what to change, e.g. "warmer color palette", "make headline read FRESH DROP", "add gym setting"'
                        className="min-h-[72px] text-sm flex-1"
                        disabled={editingTileIds.has(previewTile.id)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            (e.metaKey || e.ctrlKey) &&
                            !editingTileIds.has(previewTile.id) &&
                            editPrompt.trim()
                          ) {
                            e.preventDefault();
                            void handleEditSubmit();
                          }
                        }}
                        data-testid="input-edit-prompt"
                      />
                      <Button
                        size="sm"
                        className="h-9 self-end"
                        onClick={handleEditSubmit}
                        disabled={editingTileIds.has(previewTile.id) || !editPrompt.trim()}
                        data-testid="button-edit-submit"
                      >
                        {editingTileIds.has(previewTile.id) ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> {imageEditPhaseLabel(tileEditPhases[previewTile.id] ?? null)}</>
                        ) : (
                          <><Wand2 className="w-3.5 h-3.5 mr-1" /> Edit</>
                        )}
                      </Button>
                    </div>
                    {editError && (
                      <p className="text-xs text-red-500" data-testid="text-edit-error">{editError}</p>
                    )}
                  </div>
                )}

                {editResultUrl && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                      {editError ? "Edit complete — awaiting save retry." : "Edit ready for review — accept to keep."}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const pending = findPendingImageEdit(previewTile.imageUrl);
                          if (pending) clearPendingImageEdit(pending.jobId);
                          setEditResultUrl(null); setEditError(null);
                        }}
                        data-testid="button-edit-try-again"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Start a new edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleEditAccept}
                        data-testid="button-edit-accept"
                      >
                        <Check className="w-3 h-3 mr-1" /> Accept
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin-only: KC design prompt editor */}
      <Dialog open={!!editingUnit} onOpenChange={(v) => { if (!v) setEditingUnit(null); }}>
        <DialogContent className="max-w-2xl" data-testid="dialog-edit-kc-prompt">
          <DialogHeader>
            <DialogTitle>KC design prompt — {editingUnit?.name ?? ""}</DialogTitle>
            <DialogDescription>
              The per-template instruction sent to <code>SIM_AD_PROMPT_URL</code> alongside
              the product brief and creative direction. Templates without a prompt block
              KC generation.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value.slice(0, 20000))}
            placeholder="Describe how the AI should design ads for this template…"
            className="min-h-[260px] font-mono text-xs"
            data-testid="textarea-kc-prompt-edit"
          />
          <div className="text-[11px] text-muted-foreground tabular-nums text-right">
            {draftPrompt.length}/20000
          </div>
          <div className="flex items-start justify-between gap-4 rounded-md border bg-muted/30 p-3">
            <div className="space-y-1">
              <Label htmlFor="kc-use-template-image" className="text-sm font-medium cursor-pointer">
                Use template image as visual reference
              </Label>
              <p className="text-xs text-muted-foreground">
                When on, the template's example image is sent to the AI as the primary visual style reference (first image), ahead of the brand logo and product photos. Turn off for prompt-only templates.
              </p>
              {editingUnit && toImageRecordsAE(editingUnit.exampleImages).length === 0 && !editingUnit.adgenExampleImageUrl && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  This template has no example image set — the toggle has no effect until one is uploaded on the ad unit page.
                </p>
              )}
            </div>
            <Switch
              id="kc-use-template-image"
              checked={draftUseTemplateImage}
              onCheckedChange={setDraftUseTemplateImage}
              data-testid="switch-kc-use-template-image"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingUnit(null)}
              disabled={savingPrompt}
              data-testid="button-cancel-kc-prompt"
            >
              Cancel
            </Button>
            <Button
              onClick={savePromptEdit}
              disabled={savingPrompt}
              data-testid="button-save-kc-prompt"
            >
              {savingPrompt && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCard({
  unit, checked, disabled, onToggle,
  isAdmin, hasPrompt, onEditPrompt,
}: {
  unit: AdUnitType;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  hasPrompt: boolean;
  onEditPrompt: () => void;
}) {
  const imageRecords = toImageRecordsAE(unit.exampleImages);
  const thumb = displayUrl(imageRecords[0] ?? null);
  const shortName = shortNameOf(unit.slug, unit.name);
  const showOriginal = shortName !== unit.name;

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <div className="relative">
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onToggle}
            disabled={disabled}
            aria-pressed={checked}
            className={[
              "group relative flex flex-col rounded-lg overflow-hidden border-2 text-left transition-all w-full",
              checked
                ? "border-primary ring-2 ring-primary/30 shadow-sm"
                : "border-border/50 hover:border-border hover:shadow-sm",
              disabled ? "opacity-60 pointer-events-none" : "cursor-pointer",
            ].join(" ")}
            data-testid={`card-template-${unit.id}`}
          >
            <div className="aspect-square bg-muted/30 relative overflow-hidden">
              {thumb ? (
                <AdImage
                  src={thumb}
                  alt={shortName}
                  className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Megaphone className="h-6 w-6 text-muted-foreground/30" />
                </div>
              )}
              {checked && (
                <div
                  className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow"
                  data-testid={`indicator-checked-${unit.id}`}
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </div>
              )}
              {checked && (
                <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
              )}
            </div>
            <div className="px-2.5 py-2 bg-card">
              <div
                className="text-[12px] font-semibold leading-tight tracking-tight line-clamp-2"
                data-testid={`text-template-short-${unit.id}`}
              >
                {shortName}
              </div>
            </div>
          </button>
        </TooltipTrigger>

        {/* Admin-only: edit KC design prompt for this template */}
        {isAdmin && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEditPrompt(); }}
            className={[
              "absolute top-2 left-2 z-10 h-6 px-1.5 rounded-md flex items-center gap-1 text-[10px] font-medium shadow-sm transition-colors",
              hasPrompt
                ? "bg-background/90 text-foreground border border-border hover:bg-background"
                : "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            ].join(" ")}
            title={hasPrompt ? "Edit KC design prompt" : "Set KC design prompt (required)"}
            data-testid={`button-edit-kc-prompt-${unit.id}`}
          >
            <Pencil className="h-3 w-3" />
            {hasPrompt ? "Prompt" : "Not set"}
          </button>
        )}
        </div>
        {showOriginal && (
          <TooltipContent side="bottom" className="text-xs max-w-xs">
            {unit.name}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
